import type { PartnerCredentialSummary } from "@tdelivr/api-client/generated/partner-api/partner-credential.js";
import type { StateClassKey } from "@tdelivr/design-tokens";

import { asDay, type BoardRow } from "./board.ts";

/**
 * What the integrations board does to the summaries API-02 returns, away from any rendering.
 *
 * Built to `17 WP16 Tenant Admin.dc.html`, page `integrations`, against API-02
 * `GET /v1/partner-credentials` — published by
 * [ADR 0044](../../../docs/adr/0044-a-tenants-partner-credentials-are-listed-under-v1-as-a-mask-and-a-state.md)
 * because every operation of that service was under `/internal/v1` and all three took a `partnerId`
 * minted by whoever registered the partner.
 *
 * **`···9F2A` is the client identifier's last four characters and not the secret's, and that
 * difference is the page's own designed sentence.** Its closing note reads "A secret is shown once
 * when created, then only its last four characters", and that is not what API-02 keeps: the
 * plaintext is returned once by the issue command and stored only as an argon2id hash, from which
 * no four characters can be recovered. The last four of the **client identifier** are publishable
 * and are what the board draws, with the word `client` rather than `key` so the cell does not claim
 * to be something it is not. Q-150.
 *
 * **The `Webhook` column has no source, and the reason is a service boundary rather than a missing
 * field.** The design's third column is `delivered 99.8%`, `daily 02:00` and
 * `500 Internal Server Error` — webhook delivery outcomes, which belong to API-03, the
 * webhook-delivery service. Every one of its four operations is under `/internal/v1`, it is not an
 * owner of this page, and deciding its public surface is a separate decision for that service to
 * take, exactly as ADR 0044 was API-02's. So the cell is drawn as the design's own `—` with the
 * measured reason on it. Q-153.
 *
 * **None of the four tiles has a source either.** `Keys · 4` and `Webhooks · 6` are counts no
 * operation answers — the listing publishes a `nextAfter` and no total, which is what
 * `boundedNote` exists to say honestly. `Failing endpoint · 1 · 500 since 11:02` is Q-153's
 * webhook outcome. `Keys expiring · 1 · 14 days` is the only one with a source, `expiresAt`, and it
 * is a count of rows the board already shows and badges.
 */

/** How close to expiry a credential has to be before the board says so. */
export const EXPIRING_WITHIN_DAYS = 30;

const DAY_MS = 86_400_000;

/** Whole days from now until this credential expires. Negative once it has. */
export function daysUntilExpiry(credential: PartnerCredentialSummary, now: number): number {
  return Math.floor((Date.parse(credential.expiresAt) - now) / DAY_MS);
}

/**
 * The design's `Credential` column: `key ···9F2A · expires 14 days`, in the one word it can prove.
 *
 * The middle-dot clause is the design's own and appears only when it is true, which is the same
 * rule `boundedNote` follows: a board that wrote `· expires 365 days` on every row would be saying
 * something about every key rather than about the one that needs attention.
 */
export function credentialText(credential: PartnerCredentialSummary, now: number): string {
  const masked = `client ···${credential.clientIdSuffix}`;
  const days = daysUntilExpiry(credential, now);
  if (days < 0) return `${masked} · expired`;
  if (days > EXPIRING_WITHIN_DAYS) return masked;
  return `${masked} · expires ${String(days)} days`;
}

/**
 * The state class and the word.
 *
 * One of the design's three words is reachable, `key expiring`; the other two are Q-153's. What
 * API-02 publishes is a three-value status, an expiry and a revocation instant, and
 * `isCredentialUsable` in its own domain says what makes a credential live: active, unexpired, and
 * carrying the scope being asked for. The first two are the two readings below that the status
 * alone would get wrong.
 */
export function stateOf(
  credential: PartnerCredentialSummary,
  now: number,
): { classKey: StateClassKey; label: string; title?: string } {
  if (credential.status === "revoked") {
    return {
      classKey: "closed",
      label: "revoked",
      title:
        credential.revokedAt === null
          ? "Revoked. API-02 records no instant for it on this row."
          : `Revoked on ${asDay(credential.revokedAt)}. A revoked credential is refused by introspection whatever its expiry says.`,
    };
  }
  if (daysUntilExpiry(credential, now) < 0) {
    return {
      classKey: "closed",
      label: "expired",
      title: `Expired on ${asDay(credential.expiresAt)}. API-02 holds the row as ${credential.status} and compares the expiry at every introspection, so the stored status is not the whole state.`,
    };
  }
  if (credential.status === "pending_approval") {
    return {
      classKey: "human",
      label: "awaiting approval",
      title:
        "Issued and not yet activated. API-02 requires two approval references to issue and a separate transition to activate, so a credential in this state cannot be used by the partner it belongs to.",
    };
  }
  if (daysUntilExpiry(credential, now) <= EXPIRING_WITHIN_DAYS) {
    return { classKey: "caution", label: "key expiring" };
  }
  return {
    classKey: "done",
    label: "active",
    title:
      "Active and inside its expiry. The design's word here is `healthy`, which is a verdict about webhook delivery rather than about a credential, and nothing reachable publishes one. Q-153.",
  };
}

/** One row of the integrations board. */
export function credentialRow(credential: PartnerCredentialSummary, now: number): BoardRow {
  return {
    id: credential.credentialId,
    cells: [
      { text: credential.displayName },
      { text: credentialText(credential, now), mono: true },
      {
        text: "",
        absent: true,
        title:
          "API-03, the webhook-delivery service, holds a webhook's delivery outcome and publishes only internal operations. It is not an owner of this page, and deciding its public surface is that service's own decision. Q-153.",
      },
    ],
    state: stateOf(credential, now),
    detail: [
      { label: "Expires", value: asDay(credential.expiresAt) },
      { label: "Partner", value: credential.partnerId, mono: true },
      ...(credential.revokedAt === null
        ? []
        : [{ label: "Revoked", value: asDay(credential.revokedAt) }]),
    ],
    history: [
      { label: "Created", value: asDay(credential.createdAt) },
      { label: "Last changed", value: asDay(credential.updatedAt) },
      { label: "Version", value: `v${String(credential.version)}` },
    ],
    // The design's own form icon for this page, which is where its detail panel takes the glyph.
    glyph: "hub",
  };
}

/** The states this listing can actually answer, in the order `stateOf` decides them. */
export const CREDENTIAL_STATES: readonly string[] = [
  "revoked",
  "expired",
  "awaiting approval",
  "key expiring",
  "active",
];

/** Which credentials a chosen state leaves on the board. Client-side: the listing has no filter. */
export function filterCredentials(
  credentials: readonly PartnerCredentialSummary[],
  state: string,
  now: number,
): readonly PartnerCredentialSummary[] {
  if (state === "") return credentials;
  return credentials.filter((credential) => stateOf(credential, now).label === state);
}
